import { AppointmentPractitioner } from '@dreryk/egabinet-appointment-frontend-api';
import { WaitlistEntry } from '@dreryk/egabinet-waitlist-frontend-api';

import { TimetableSlotSelectionEvent } from '@dreryk/react-calendar';
import {
   AlertIcon,
   IconFillColor,
   IconSize,
   IconWrapper,
   Modal,
   ModalCancelButton,
   ModalCloseButton,
   ModalMaxHeight,
   ModalWidth,
   StyledFlex,
   Text,
   ValidateFormEventInterceptor,
} from '@dreryk/react-components';
import { useTranslate } from '@dreryk/react-common';

import { VideoConsultationPatientContactUpdateShowModalEventInterceptor } from '~libs.desktop.video-consultation';

import { appointmentsTranslationKeys, waitlistEntryTranslationKeys } from '../../../../translations';
import { modalConfirmExitConfig } from '../shared/AppointmentCreateModal.utils';

import { AppointmentWaitlistCreateFormController } from './AppointmentWaitlistCreateForm.types';
import { appointmentWaitlistCreateFormInitializer } from './AppointmentWaitlistCreateForm.utils';
import { AppointmentWaitlistCreateFormLoader } from './AppointmentWaitlistCreateForm.loader';
import { AppointmentWaitlistCreateForm } from './AppointmentWaitlistCreateForm';

type AppointmentWaitlistCreateModalConfirmButtonRenderer = (
   renderProps: Pick<AppointmentWaitlistCreateFormController, 'handleSubmit'> & {
      setAppointmentTimeSlotOccupiedError: VoidFunction;
   }
) => JSX.Element;

export type AppointmentWaitlistCreateModalProps = {
   readonly selectedSlot: TimetableSlotSelectionEvent<AppointmentPractitioner>;
   readonly onClose: VoidFunction;
   readonly confirmButton: AppointmentWaitlistCreateModalConfirmButtonRenderer;
   readonly waitlistEntry: WaitlistEntry;
   readonly urgent: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const AppointmentWaitlistCreateModal = (props: AppointmentWaitlistCreateModalProps): JSX.Element => {
   const translate = useTranslate();
   return (
      <Modal
         testId={'appointmentWaitlistPatientBook'}
         title={appointmentsTranslationKeys.create.modal.title.register}
         width={ModalWidth.WIDE}
         height={ModalMaxHeight.TALL}
      >
         <StyledFlex direction={'column'} gap={'24px'}>
            <AppointmentWaitlistCreateFormLoader
               selectedSlot={props.selectedSlot}
               patientId={props.waitlistEntry.patient.id.asNumber()}
               practitionerId={props.waitlistEntry.practitioner.id}
               receptionServiceId={props.waitlistEntry.receptionService?.id}
            >
               {({
                  practitioners,
                  patient,
                  healthcareProviderLocations,
                  healthcareProviderLocationIdentifierFromSchedule,
                  receptionService,
               }) => (
                  <>
                     {props.urgent ? (
                        <StyledFlex gap={'4px'} alignItems={'center'}>
                           <IconWrapper testId={'urgent'} fillColor={IconFillColor.ERROR} size={IconSize.NORMAL}>
                              <AlertIcon />
                           </IconWrapper>
                           <Text testId={'urgent'}>{translate(waitlistEntryTranslationKeys.urgent)}</Text>
                        </StyledFlex>
                     ) : null}
                     <AppointmentWaitlistCreateForm
                        initializer={appointmentWaitlistCreateFormInitializer({
                           selectedSlot: props.selectedSlot,
                           healthcareLocationProviders: healthcareProviderLocations,
                           healthcareProviderLocationIdentifierFromSchedule: healthcareProviderLocationIdentifierFromSchedule,
                           waitlistEntry: props.waitlistEntry,
                           practitioners: practitioners,
                           translate: translate,
                           patient: patient,
                           receptionService: receptionService,
                        })}
                        practitioners={practitioners}
                        waitlistEntry={props.waitlistEntry}
                        healthcareProviderLocations={healthcareProviderLocations}
                     >
                        {({ handleSubmit, getValue, trigger, setAppointmentTimeSlotOccupiedError, formState }) => {
                           const patientId = getValue('patient')?.id ?? null;
                           const videoConsultationEnabled = Boolean(getValue('videoConsultation'));
                           return (
                              <>
                                 <ValidateFormEventInterceptor trigger={trigger} silent>
                                    <VideoConsultationPatientContactUpdateShowModalEventInterceptor
                                       patientId={patientId}
                                       shouldShowModal={videoConsultationEnabled}
                                    >
                                       {props.confirmButton({
                                          handleSubmit: handleSubmit,
                                          setAppointmentTimeSlotOccupiedError: setAppointmentTimeSlotOccupiedError,
                                       })}
                                    </VideoConsultationPatientContactUpdateShowModalEventInterceptor>
                                 </ValidateFormEventInterceptor>

                                 <ModalCancelButton
                                    onClick={props.onClose}
                                    shouldConfirmExit={modalConfirmExitConfig(formState.isDirty)}
                                 />
                                 <ModalCloseButton
                                    onClick={props.onClose}
                                    shouldConfirmExit={modalConfirmExitConfig(formState.isDirty)}
                                 />
                              </>
                           );
                        }}
                     </AppointmentWaitlistCreateForm>
                  </>
               )}
            </AppointmentWaitlistCreateFormLoader>
         </StyledFlex>
      </Modal>
   );
};
